'use client';

import type { CSSProperties } from 'react';
import styled from 'styled-components';
import { isSafari } from '@frameio/vapor';
import { getNativeStringFromUnified } from '../components/EmojiPickerPopover/data/data';
import {
  type EmojiDataProp,
  resolveEmoji,
  toEmojiData,
} from '../components/EmojiPickerPopover/data/emojiData';

export type { EmojiDataProp as EmojiType } from '../components/EmojiPickerPopover/data/emojiData';

export const EmojiContainer = styled.div<{ $isSmall?: boolean }>`
  align-items: center;
  display: inline-flex;
  font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    system-ui;
`;

type AnyEmojiFormat =
  | string
  | EmojiDataProp
  | {
      id: string;
      name: string;
      colons: string;
    };

type EmojiWrapperProps = {
  /** Note that the Frame.io component `Emoji` provides a default size */
  size?: number;
  style?: CSSProperties;
  emoji: AnyEmojiFormat;
};

function resolveAmbiguousEmojiIdentifier(anyEmojiFormat: AnyEmojiFormat) {
  const id =
    typeof anyEmojiFormat === 'string'
      ? anyEmojiFormat.replace(/^:|:$/g, '')
      : anyEmojiFormat.id;
  const resolvedEmoji = resolveEmoji(id);
  const emojiData = resolvedEmoji ? toEmojiData(resolvedEmoji) : null;
  let nativeEmoji = emojiData?.native ?? null;
  if (typeof anyEmojiFormat !== 'string') {
    if ('native' in anyEmojiFormat) {
      nativeEmoji ??= anyEmojiFormat.native ?? null;
    }
    if ('unified' in anyEmojiFormat && anyEmojiFormat.unified) {
      nativeEmoji = getNativeStringFromUnified(anyEmojiFormat.unified);
    }
  }
  return nativeEmoji;
}

export type EmojiProps = EmojiWrapperProps;

export const Emoji = ({ emoji, size = 16, style }: EmojiWrapperProps) => {
  let fontSize = size;
  const nativeEmoji = resolveAmbiguousEmojiIdentifier(emoji);
  const isSmall = size < 24;

  if (isSmall && isSafari()) {
    fontSize = size * 0.8;
  }

  return (
    <EmojiContainer
      $isSmall={isSmall}
      style={{
        ...style,
        fontSize: `${fontSize}px`,
      }}
    >
      {nativeEmoji}
    </EmojiContainer>
  );
};
